import { createSlice } from "@reduxjs/toolkit"
import {
  addOfferBanner,
  addOfferSlide,
  deleteOfferBanner,
  deleteOfferSlide,
  getAllOfferBanners,
  getOfferBannerDetails,
  getOfferSlideDetails,
  updateOfferBanner,
  updateOfferBannerStatus,
  updateOfferSlide,
  updateOfferSlideStatus,
} from "store/actions/offerBannerAction"

const initialState = {
  loading: true,
  error: null,
  isLoading: false,
  actionType: null,
  message: null,
  itemsPerPage: 10,
  totalCount: null,
  totalPages: null,
  currentPage: 1,
  isUpdating: false,
  bannerLists: [],
  bannerDetails: {},
  slideDetails: {},
}

const offerBannersSlice = createSlice({
  name: "OfferBanners",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllOfferBanners.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(getAllOfferBanners.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          const { totalCount, totalPages, currentPage } = action.payload
          state.bannerLists = data
          state.totalCount = totalCount
          state.totalPages = totalPages
          state.currentPage = currentPage
        }
      })
      .addCase(getAllOfferBanners.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(addOfferBanner.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
        state.message = null
      })
      .addCase(addOfferBanner.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, data } = action.payload
        if (status) {
          if (data) {
            state.bannerLists.push(data)
          }
          state.actionType = "BANNER_ADDED_SUCCESS"
          state.message = action.payload.msg
          action.meta.arg.handleClose()
          if (state.totalCount !== null) {
            state.totalCount += 1
          }
        } else {
          state.error = action.payload.msg
          state.actionType = "BANNER_ADDED_FAILED"
        }
      })
      .addCase(addOfferBanner.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(deleteOfferBanner.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(deleteOfferBanner.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, msg } = action.payload
        const { id } = action.meta.arg
        if (status) {
          state.bannerLists = state.bannerLists.filter(item => item._id !== id)
          state.error = null
          state.actionType = "BANNER_DELETE_SUCCESS"
          state.message = msg
          action.meta.arg.setShowDeleteModal(false)
          if (state.totalCount !== null) {
            state.totalCount -= 1
          }
        } else {
          state.actionType = "BANNER_DELETE_FAILED"
          state.error = msg
        }
      })
      .addCase(deleteOfferBanner.rejected, state => {
        state.isLoading = false
      })
      .addCase(getOfferBannerDetails.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(getOfferBannerDetails.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, data } = action.payload
        if (status) {
          state.bannerDetails = data
          state.actionType = "GET_BANNER_DETAILS_SUCCESS"
        }
      })
      .addCase(getOfferBannerDetails.rejected, state => {
        state.isLoading = false
        state.actionType = "GET_BANNER_DETAILS_FAILED"
      })

    builder
      .addCase(updateOfferBanner.pending, state => {
        state.error = null
        state.isUpdating = true
        state.actionType = null
        state.message = null
      })
      .addCase(updateOfferBanner.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, name, nameAr } = action.meta.arg
          state.actionType = "BANNER_UPDATED_SUCCESS"
          state.bannerLists = state.bannerLists.map(banner =>
            banner._id === id ? { ...banner, name, nameAr } : banner
          )
          state.message = action.payload.msg
          action.meta.arg.handleClose()
        } else {
          state.error = action.payload.msg
          state.actionType = "BANNER_UPDATED_FAILED"
        }
      })
      .addCase(updateOfferBanner.rejected, state => {
        state.isUpdating = false
      })

    builder
      .addCase(updateOfferBannerStatus.pending, state => {
        state.error = null
        state.actionType = null
        state.isUpdating = true
      })
      .addCase(updateOfferBannerStatus.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, status } = action.meta.arg
          const updatedBanner = state.bannerLists.find(banner => banner._id === id)
          const updatedBrandId = updatedBanner ? updatedBanner.brand : null
          const updatedCountryCode = updatedBanner
            ? updatedBanner.countryCode
            : null

          state.bannerLists = state.bannerLists.map(banner =>
            banner.brand === updatedBrandId &&
            banner.countryCode === updatedCountryCode
              ? banner._id === id
                ? { ...banner, status: Number(status) }
                : { ...banner, status: 0 }
              : banner
          )

          state.message = action.payload.msg
          state.actionType = "BANNER_STATUS_UPDATE_SUCCESS"
        } else {
          state.actionType = "BANNER_STATUS_UPDATE_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(updateOfferBannerStatus.rejected, state => {
        state.isUpdating = false
      })

    builder
      .addCase(addOfferSlide.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
        state.message = null
      })
      .addCase(addOfferSlide.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "SLIDE_ADDED_SUCCESS"
          state.message = action.payload.msg
        } else {
          state.error = action.payload.msg
          state.actionType = "SLIDE_ADDED_FAILED"
        }
      })
      .addCase(addOfferSlide.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(updateOfferSlideStatus.pending, state => {
        state.error = null
        state.actionType = null
        state.isUpdating = true
      })
      .addCase(updateOfferSlideStatus.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, status, bannerId } = action.meta.arg
          state.bannerLists = state.bannerLists.map(banner => {
            if (banner._id === bannerId) {
              banner.slides = banner.slides.map(slide => {
                if (slide._id === id) {
                  slide.status = Number(status)
                }
                return slide
              })
            }
            return banner
          })
          state.actionType = "SLIDE_STATUS_UPDATE_SUCCESS"
          state.message = action.payload.msg
        } else {
          state.actionType = "SLIDE_STATUS_UPDATE_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(updateOfferSlideStatus.rejected, state => {
        state.isUpdating = false
      })

    builder
      .addCase(deleteOfferSlide.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(deleteOfferSlide.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, msg } = action.payload
        const { id, selectedSlideBannerId, setShowSlideDeleteModal } =
          action.meta.arg
        if (status) {
          state.bannerLists = state.bannerLists.map(banner => {
            if (banner._id === selectedSlideBannerId) {
              banner.slides = banner.slides.filter(slide => slide._id !== id)
            }
            return banner
          })

          state.actionType = "SLIDE_DELETE_SUCCESS"
          state.message = msg
          setShowSlideDeleteModal(false)
        } else {
          state.actionType = "SLIDE_DELETE_FAILED"
          state.error = msg
        }
      })
      .addCase(deleteOfferSlide.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(getOfferSlideDetails.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(getOfferSlideDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          state.slideDetails = data
        }
      })
      .addCase(getOfferSlideDetails.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(updateOfferSlide.pending, state => {
        state.error = null
        state.isUpdating = true
        state.actionType = null
        state.message = null
      })
      .addCase(updateOfferSlide.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          state.actionType = "SLIDE_UPDATED_SUCCESS"
          state.message = action.payload.msg
        } else {
          state.error = action.payload.msg
          state.actionType = "SLIDE_UPDATED_FAILED"
        }
      })
      .addCase(updateOfferSlide.rejected, state => {
        state.isUpdating = false
      })
  },
})

export default offerBannersSlice.reducer
